/**
 * ChromeBox — the styled container for canvas-overlay chrome (the top-right
 * "Tidy layout / Auto-arrange" toolbar and the bottom-center reparent-undo
 * toast). One react-strict-dom file for web + native: `html.div` renders a real
 * <div> on web and an RN host on native. `boxShadow` (and container-level
 * text props) apply on web and are dropped on native, which is the intended
 * platform behaviour.
 */
import type { ReactNode } from "react";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  spacing,
  microSpacing,
  radii,
} from "@duro-app/tokens/tokens/spacing.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { typeScale } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";

const s = css.create({
  toolbar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    backgroundColor: colors.bg,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.sm,
    paddingTop: microSpacing.px5,
    paddingBottom: microSpacing.px5,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    fontSize: typeScale.fontSize2,
    color: colors.text,
    boxShadow: shadows.sm,
  },
  toast: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.ms,
    backgroundColor: colors.contrastSurface,
    color: colors.onContrastSurface,
    borderRadius: radii.sm,
    paddingTop: microSpacing.px6,
    paddingBottom: microSpacing.px6,
    paddingLeft: spacing.ms,
    paddingRight: microSpacing.px6,
    fontSize: typeScale.fontSize2,
    boxShadow: shadows.md,
  },
});

export function ChromeBox({
  variant,
  children,
}: {
  variant: "toolbar" | "toast";
  children: ReactNode;
}) {
  return (
    <html.div style={variant === "toolbar" ? s.toolbar : s.toast}>
      {children}
    </html.div>
  );
}
